import { doc, onSnapshot, Unsubscribe, getFirestore, DocumentSnapshot } from 'firebase/firestore';

import { getFirebaseWebApp } from 'src/auth/store/firebase/lib';

import { handleInventoryBusinessField } from './inventory-sync.service';
import { handleDeviceControlBusinessField } from './business-control-local-state.service';
import {
  type BusinessDeviceSyncFields,
  handleDeviceSyncBusinessFields,
} from './device-sync-v4.service';

type BusinessDocumentListenerOptions = {
  enableInventorySync?: boolean;
  enableDeviceControl?: boolean;
  enableDeviceSync?: boolean;
  onDeviceSyncFieldsChange?: (fields: BusinessDeviceSyncFields) => Promise<void>;
  onProviderDetailsVersionChange?: (version: number) => void;
};

function readVersion(value: unknown): number | null {
  if (!value || typeof value !== 'object') return null;
  const { version } = value as { version?: unknown };
  return typeof version === 'number' ? version : null;
}

export function startBusinessDocumentListener(
  businessId: string,
  options: BusinessDocumentListenerOptions = {}
): Unsubscribe {
  if (!businessId) return () => {};

  const {
    enableInventorySync = true,
    enableDeviceControl = true,
    enableDeviceSync = true,
    onProviderDetailsVersionChange,
    onDeviceSyncFieldsChange,
  } = options;
  const db = getFirestore(getFirebaseWebApp());
  const docRef = doc(db, 'business', businessId);

  return onSnapshot(
    docRef,
    async (snapshot: DocumentSnapshot) => {
      if (!snapshot.exists()) return;

      const data = snapshot.data();

      if (enableInventorySync) await handleInventoryBusinessField(data?.inventory);
      if (enableDeviceSync) {
        await (onDeviceSyncFieldsChange ?? handleDeviceSyncBusinessFields)({
          businessProfile: data?.businessProfile,
          userProfile: data?.userProfile,
          terminals: data?.terminals,
          businessDevices: data?.businessDevices,
          providerDetails: data?.providerDetails,
          recurringPlans: data?.recurringPlans,
        });
      }
      const providerDetailsVersion = readVersion(data?.providerDetails);
      if (providerDetailsVersion !== null) {
        onProviderDetailsVersionChange?.(providerDetailsVersion);
      }
      if (enableDeviceControl) {
        await handleDeviceControlBusinessField({
          businessId,
          deviceControl: data?.deviceControl,
        });
      }
    },
    (err) => {
      console.error('[BusinessDocumentListener] Listener error:', err);
    }
  );
}
